import { useDispatch, useSelector } from "react-redux";
import { useCallback, useMemo } from "react";
import { TreeTable, type TreeTableColumnDef } from "../../../../../common/components/TreeTable/TreeTable";
import { type SRPSeriesTreeNode } from "../../../../models";
import { useUnitMode } from "../../../../../feature/UnitMode/useUnitMode";
import {
  setCategoryFilterFromSeriesTree,
  toggleSeriesTreeNodeAction,
} from "../../../../redux/reducer/seriesTreeReducer";
import { LinkButton, LinkType } from "../../../../../common/components/Link";
import { Trans } from "react-i18next";
import { selectSeriesTree } from "../../../../redux/seriesTreeSelectors";
import { UnitMode } from "../../../../../feature/UnitMode/UnitMode";
import classNames from "classnames";
import { useSearchPageType } from "~/Search/useSearchPageType";
import { renderTechValue } from "~/utils/renderTechValue";

export const SeriesTreeView = () => {
  const dispatch = useDispatch();
  const seriesTreeData = useSelector(selectSeriesTree);
  const unitMode = useUnitMode();
  const pageType = useSearchPageType();

  const onCategoryClick = useCallback(
    (node: SRPSeriesTreeNode) => dispatch(setCategoryFilterFromSeriesTree(node)),
    [pageType],
  );

  const columnConfig: TreeTableColumnDef<SRPSeriesTreeNode>[] = useMemo(() => {
    const getShaftLabel = (node: SRPSeriesTreeNode) => {
      let min, max;
      if (unitMode === UnitMode.Imperial) {
        min = node.minImperialDiameter ? renderTechValue(node.minImperialDiameter.toString(), true) : "";
        max = node.maxImperialDiameter ? renderTechValue(node.maxImperialDiameter.toString(), true) : "";
      } else {
        min = node.minDiameter;
        max = node.maxDiameter;
      }
      if (!min || !max) {
        return "";
      }
      return (
        <div className={classNames(node.isImperialConverted && unitMode === UnitMode.Imperial && "f-info-color")}>
          {min} - {max}
        </div>
      );
    };

    return [
      {
        title: (
          <strong>
            <Trans i18nKey={"seriesTree.header.productGroup"} />
          </strong>
        ),
        width: "50%",
        render: (node) => (
          <>
            <LinkButton onClick={() => onCategoryClick(node)} variant={LinkType.NAVIGATION}>
              {node.name}
            </LinkButton>
            <span> ({node.count})</span>
          </>
        ),
      },
      {
        title: (
          <strong>
            <Trans
              i18nKey={"seriesTree.header.shaftDiameter"}
              values={{ unit: unitMode === UnitMode.Metric ? "mm" : "in" }}
            />
          </strong>
        ),
        width: "50%",
        render: getShaftLabel,
      },
    ];
  }, [unitMode]);

  const onToggleExpand = useCallback(
    (nodeId: string, isExpanded: boolean) => dispatch(toggleSeriesTreeNodeAction(nodeId, isExpanded)),
    [],
  );

  if (!seriesTreeData.length) {
    return null;
  }

  return (
    <div>
      <TreeTable data={seriesTreeData} colDefs={columnConfig} onToggleExpand={onToggleExpand} />
    </div>
  );
};
